import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { suggestTrackRecord, type TrackSuggestion } from "api/dashboard";

interface TrackRecordSearchInputProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  label?: string;
  placeholder?: string;
  inputId?: string;
}

const DEBOUNCE_MS = 250;
const SUGGEST_LIMIT = 10;
const MIN_CHARS = 1;

/**
 * Text input with autocomplete dropdown for the dashboard Track flow.
 * Suggests mark names and TM/Application numbers from the backend as the user types.
 * Enter submits the current input value (uses the parent's onSubmit) unless a suggestion
 * is highlighted via arrow keys, in which case Enter selects the suggestion first.
 */
export const TrackRecordSearchInput = ({
  value,
  onChange,
  onSubmit,
  label = "Mark or Application Number",
  placeholder = "Text Here",
  inputId,
}: TrackRecordSearchInputProps): JSX.Element => {
  const generatedId = useId();
  const id = inputId ?? `track-search-input-${generatedId}`;
  const listboxId = `${id}-listbox`;

  const [suggestions, setSuggestions] = useState<TrackSuggestion[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [loading, setLoading] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Snapshot of the last value the user picked; used to suppress the suggest call right after picking. */
  const lastPickedValueRef = useRef<string | null>(null);

  const trimmed = value.trim();

  useEffect(() => {
    if (trimmed.length < MIN_CHARS) {
      setSuggestions([]);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }
    if (lastPickedValueRef.current === trimmed) {
      setSuggestions([]);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      void suggestTrackRecord(trimmed, SUGGEST_LIMIT, controller.signal)
        .then((res) => {
          setSuggestions(res.suggestions);
          setActiveIndex(-1);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          setSuggestions([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showDropdown = useMemo(
    () => open && trimmed.length >= MIN_CHARS && (loading || suggestions.length > 0),
    [open, trimmed.length, loading, suggestions.length]
  );

  const handlePick = useCallback(
    (item: TrackSuggestion): void => {
      lastPickedValueRef.current = item.value;
      onChange(item.value);
      setOpen(false);
      setSuggestions([]);
      setActiveIndex(-1);
      inputRef.current?.focus();
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (showDropdown && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) =>
          i <= 0 ? suggestions.length - 1 : i - 1
        );
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        const picked = suggestions[activeIndex];
        if (picked) handlePick(picked);
        return;
      }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      setOpen(false);
      onSubmit();
    }
  };

  return (
    <div
      className="flex w-full max-w-full flex-col items-start gap-1"
      ref={containerRef}
    >
      <label
        className="w-full text-xs font-normal leading-5 text-[#222]"
        htmlFor={id}
      >
        {label}
      </label>
      <div className="relative w-full">
        <div className="flex w-full items-center rounded-md border border-[#eee] bg-white px-[10px] py-2">
          <input
            id={id}
            ref={inputRef}
            type="text"
            className="bg-transparent w-full flex-1 text-xs font-normal leading-5 text-[#222] outline-none placeholder:text-[#6d6d6d]"
            value={value}
            onChange={(e) => {
              lastPickedValueRef.current = null;
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
            }
            autoComplete="off"
          />
        </div>

        {showDropdown && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-[#eee] bg-white shadow-md"
          >
            {loading && suggestions.length === 0 ? (
              <li className="px-3 py-2 text-xs leading-5 text-[#6d6d6d]">
                Searching…
              </li>
            ) : (
              suggestions.map((item, idx) => {
                const isActive = idx === activeIndex;
                const optionId = `${listboxId}-opt-${idx}`;
                return (
                  <li
                    key={`${item.kind}-${item.value}`}
                    id={optionId}
                    role="option"
                    aria-selected={isActive}
                    className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs leading-5 ${
                      isActive ? "bg-[#f5f5f5] text-[#222]" : "text-[#222] hover:bg-[#fafafa]"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handlePick(item);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <span className="truncate">{item.value}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-[#6d6d6d]">
                      {item.kind === "mark" ? "Mark" : "App. No."}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TrackRecordSearchInput;
